/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Button, Icon, Grid, Modal, Segment } from 'semantic-ui-react';

// Actions
import { fetchM } from '../actions/fetchM';
import { flashErrorMessage, flashSuccessMessage } from '../actions/flash';
import { TRANSLATION_FILES } from '../constants/translation';
import { cancellationCategoryLabel } from './OpportunityCancelModal';
import { formatMinorNumber } from '../utils/numbers/currencyMinor';
import Timestamp from './Timestamp';

class RefundRequestDetail extends Component {
  static contextTypes = {
    store: PropTypes.object,
  };

  static propTypes = {
    refund: PropTypes.shape({
      id: PropTypes.string.isRequired,
      opportunityID: PropTypes.string.isRequired,
      status: PropTypes.string,
      amountMinor: PropTypes.number.isRequired,
      currencyCode: PropTypes.string.isRequired,
      reason: PropTypes.string.isRequired,
      bankName: PropTypes.string.isRequired,
      bankAccountNumber: PropTypes.string.isRequired,
      bankAccountName: PropTypes.string.isRequired,
      projectName: PropTypes.string.isRequired,
      purchaserName: PropTypes.string.isRequired,
      rejectionReason: PropTypes.string,
      processedBy: PropTypes.string,
      // Date tracking parameters mapping to backend properties
      processedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    }).isRequired,
    refundToken: PropTypes.string,
    loading: PropTypes.bool,
    onSuccess: PropTypes.func,
    onUpdateOpportunity: PropTypes.func,
    t: PropTypes.func.isRequired,
    data: PropTypes.shape({
      sr_cancellation_reason: PropTypes.string,
      sr_cancellation_type: PropTypes.string,
      sr_cancellation_at: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    }),
  };

  constructor(props) {
    super(props);
    this.state = {
      isConfirmOpen: false,
      confirmType: null,
      isSubmitting: false,
      amountMinorRefunded: '',
      rejectionReason: '',
      refund: props.refund,
    };
  }

  componentDidUpdate(prevProps) {
    // Sync refund prop changes to state
    if (prevProps.refund !== this.props.refund) {
      this.setState({ refund: this.props.refund });
    }
  }

  handleOpenConfirm = (type) => {
    this.setState({
      isConfirmOpen: true,
      confirmType: type,
      amountMinorRefunded: '',
      rejectionReason: '',
    });
  }

  handleCloseConfirm = () => {
    this.setState({ isConfirmOpen: false, confirmType: null });
  }

  handleConfirmAction = async () => {
    const { refund, refundToken, t, onUpdateOpportunity } = this.props;
    const { confirmType } = this.state;
    const { store } = this.context;

    const url = `/unit_reservation_refunds/${refund.id}/status/`;
    this.setState({ isSubmitting: true });

    try {
      const action = fetchM(url, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': refundToken,
        },
        body: JSON.stringify({
          status: confirmType, // 'approved' or 'declined'
          rejectionReason: '-',
          amountMinorRefunded: confirmType === 'approved' ? (refund ? refund.amountMinor : 0) : 0,
        }),
      });

      const response = await store.dispatch(action);

      if (response) {
        // Update local state immediately to show the new status
        this.setState(prevState => ({
          refund: {
            ...prevState.refund,
            status: confirmType,
          },
        }));

        store.dispatch(flashSuccessMessage({
          header: t('Success', { ns: TRANSLATION_FILES.constants }),
          message: t('Refund status updated successfully'),
        }));

        // Update opportunity status based on refund decision
        if (onUpdateOpportunity) {
          const opportunityStatus = confirmType === 'approved' ? 'refunded' : 'refund_declined';
          onUpdateOpportunity(opportunityStatus);
        }

        if (this.props.onSuccess) {
          this.props.onSuccess();
        }
        this.handleCloseConfirm();
      }
    } catch (err) {
      store.dispatch(flashErrorMessage({
        header: t('Error', { ns: TRANSLATION_FILES.constants }),
        message: t('Error updating refund status'),
      }));
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  renderStatusBannerHeader = (status) => {
    const { t } = this.props;
    switch (status) {
      case 'approved':
        return (
          <div className="refund-header-title text-success">
            <Icon name="check circle outline" />
            <strong>{t('Refund completed')}</strong>
          </div>
        );
      case 'declined':
        return (
          <div className="refund-header-title text-danger">
            <Icon name="times circle outline" />
            <strong>{t('Refund declined')}</strong>
          </div>
        );
      default:
        return (
          <div className="refund-header-title text-warning">
            <Icon name="clock outline" />
            <strong>{t('Pending refund')}</strong>
          </div>
        );
    }
  }

  getBannerClassName = (status) => {
    switch (status) {
      case 'approved':
        return 'refund-container-completed';
      case 'declined':
        return 'refund-container-denied';
      default:
        return 'refund-container-pending';
    }
  }

  render() {
    const { t, loading, data = {} } = this.props;
    const { isConfirmOpen, confirmType, isSubmitting, refund } = this.state;

    const currentStatus = refund && refund.status;
    const isPending = currentStatus === 'pending' || !currentStatus;
    const isApprove = confirmType === 'approved';
    const currencyCode = refund && refund.currencyCode;

    // Format currency presentation values dynamically
    const displayAmount = refund
      ? formatMinorNumber(refund.amountMinor, currencyCode)
      : '0';

    return (
      <div className="refund-detail-wrapper">
        <Segment className={`refund-status-block ${this.getBannerClassName(currentStatus)}`}>
          {/* Top Banner Header Section */}
          <div className="refund-banner-top-row">
            {this.renderStatusBannerHeader(currentStatus)}
          </div>

          {/* Core Properties Field Grid List */}
          <Grid style={{ marginTop: '1rem' }} columns="equal" className="refund-fields-grid">
            <Grid.Row className="field-row">
              <Grid.Column width={6} className="field-label">{t('Requestor name')}</Grid.Column>
              <Grid.Column width={10} textAlign="right" className="field-value font-weight-bold">
                {refund.purchaserName || '-'}
              </Grid.Column>
            </Grid.Row>

            {/* CPI-1226: the scenario above the reason, same order as the cancel modal and the
                cancellation banner. */}
            <Grid.Row className="field-row">
              <Grid.Column width={6} className="field-label">{t('Cancellation category')}</Grid.Column>
              <Grid.Column width={10} textAlign="right" className="field-value">
                {cancellationCategoryLabel(data.sr_cancellation_type) ?
                  t(cancellationCategoryLabel(data.sr_cancellation_type)) : '-'}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className="field-row">
              <Grid.Column width={6} className="field-label">{t('Cancellation reason')}</Grid.Column>
              <Grid.Column width={10} textAlign="right" className="field-value">
                {data.sr_cancellation_reason || '-'}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className="field-row">
              <Grid.Column width={6} className="field-label">{t('Request received at')}</Grid.Column>
              <Grid.Column width={10} textAlign="right" className="field-value">
                {data.sr_cancellation_at ? <Timestamp timestamp={data.sr_cancellation_at} format="DD MMM YYYY, hh:mm a" /> : '-'}
              </Grid.Column>
            </Grid.Row>

            <Grid.Row className="field-row">
              <Grid.Column width={6} className="field-label">{t('Cancellation approved at')}</Grid.Column>
              <Grid.Column width={10} textAlign="right" className="field-value">
                {data.sr_cancelled_at ? <Timestamp timestamp={data.sr_cancelled_at} format="DD MMM YYYY, hh:mm a" /> : '-'}
              </Grid.Column>
            </Grid.Row>
            <hr className="divider-line" />

            <Grid.Row className="field-row">
              <Grid.Column width={6} className="field-label">{t('Refund requested at')}</Grid.Column>
              <Grid.Column width={10} textAlign="right" className="field-value">
                {refund.createdAt ? <Timestamp timestamp={refund.createdAt} format="DD MMM YYYY, hh:mm a" /> : '-'}
              </Grid.Column>
            </Grid.Row>

            {(currentStatus === 'approved') && (
              <Grid.Row className="field-row">
                <Grid.Column width={6} className="field-label">{t('Refund completed at')}</Grid.Column>
                <Grid.Column width={10} textAlign="right" className="field-value">
                  {refund.processedAt ? <Timestamp timestamp={refund.processedAt} format="DD MMM YYYY, hh:mm a" /> : '-'}
                </Grid.Column>
              </Grid.Row>
            )}

            {(currentStatus === 'declined') && (
              <Grid.Row className="field-row">
                <Grid.Column width={6} className="field-label">{t('Refund declined at')}</Grid.Column>
                <Grid.Column width={10} textAlign="right" className="field-value">
                  {refund.processedAt ? <Timestamp timestamp={refund.processedAt} format="DD MMM YYYY, hh:mm a" /> : '-'}
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>

          {/* Action Trigger Interface: Display option buttons only if the status is Pending */}
          {isPending && (
            <div className="refund-actions-footer">
              <p className="outcome-confirmation-text">
                {t('Confirm the refund outcome for')} <strong>{`${currencyCode || 'VND'} ${displayAmount}`}</strong>:
              </p>
              <div className="buttons-group">
                <Button
                  className="btn-outcome btn-complete"
                  onClick={() => this.handleOpenConfirm('approved')}
                >
                  <Icon name="checkmark" /> {t('Refund completed')}
                </Button>
                <Button
                  className="btn-outcome btn-deny"
                  onClick={() => this.handleOpenConfirm('declined')}
                >
                  <Icon name="close" /> {t('Refund declined')}
                </Button>
              </div>
            </div>
          )}
        </Segment>

        {/* Processing Control Confirm Modal */}
        <Modal
          open={isConfirmOpen}
          onClose={this.handleCloseConfirm}
          size={isApprove ? 'mini' : 'tiny'}
          closeIcon
        >
          <Modal.Header>{isApprove ? t('Approve refund request') : t('Reject refund request')}</Modal.Header>
          <Modal.Content>
            {isApprove ? (
              <div>
                <p className="confirm-approve-text">
                  {t('Are you sure you want to approve this refund of')} <strong>{`${currencyCode || 'VND'} ${displayAmount}`}</strong>?
                </p>
                <p className="approve-info-text" style={{ color: '#555555', fontSize: '0.9rem' }}>
                  {t('Once approved, refunds will be processed for all received payments.')}
                </p>
              </div>
            ) : (
              <div className="reject-refund-wrapper">
                <p className="confirm-reject-text">
                  {t('Are you sure you want to reject this refund request?')}
                </p>
                <div style={{ marginTop: '1rem' }}>
                  <Icon name="info circle" className="info-icon" style={{ color: '#0288D1', marginRight: '6px' }} />
                  <span className="reject-info-text" style={{ color: '#555555', fontSize: '0.9rem' }}>
                    {t('A notification will be sent to the agent and buyer upon confirmation.')}
                  </span>
                </div>
              </div>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button basic onClick={this.handleCloseConfirm}>{t('Cancel')}</Button>
            <Button
              color="teal"
              onClick={this.handleConfirmAction}
              loading={isSubmitting}
              disabled={loading || isSubmitting}
            >
              {t('Confirm')}
            </Button>
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}

export default withTranslation()(RefundRequestDetail);



// WEBPACK FOOTER //
// ./src/components/RefundRequestDetail.jsx